// @givmo/org-console — rescind one live invite: the confirm.
//
// A small confirm, and it earns its own file for the reason the class-specific twin it supersedes
// does: rescinding an OFFER is a different act from removing an ADMIN, and one dialog serving both
// would have to hedge its copy until it said neither thing clearly.
//
// ⚠ IT NAMES THE SUBJECT. The list it is opened from can hold several addresses that differ by a few
// characters, and the act cannot be undone — the link stops working, and the only remedy is a new
// offer with a new token, because the plaintext of the old one exists nowhere.
//
// ⚠ AND IT SAYS WHY A RESCIND WAS REFUSED, in words, in place. The confirm stays standing on a
// failure (closing would let the row reappear on the next read with nothing said), and a confirm
// that stands in silence — its spinner stopped, its subject named, its button live again — reads as
// the control not working. The sentence is the panel's (`error`): the door's own refusal where the
// panel can say it better than the server, the server's text verbatim everywhere else.
//
// DATA CONTRACT: `<prefix>-revoke-*`, where the prefix is the invites panel's own.

import { Button, Dialog, ErrorMessage, testIdProp } from '@givmo/design-system';

export interface OrgInviteRevokeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The invited address — the confirm's subject. */
  email: string;
  /** The invites panel's test-id namespace, so both surfaces share one family. */
  testIdPrefix: string;
  loading: boolean;
  /** The last rescind's refusal, in words — `null` while none stands. */
  error?: string | null;
  onConfirm: () => void;
}

export function OrgInviteRevokeDialog({
  open,
  onOpenChange,
  email,
  testIdPrefix,
  loading,
  error = null,
  onConfirm,
}: OrgInviteRevokeDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <Dialog.Content
        aria-label="Rescind invite"
        className="flex flex-col gap-16"
        testId={`${testIdPrefix}-revoke-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Rescind this invite?
        </Dialog.Title>

        <p
          className="font-sans text-s15 font-w600 tracking-givmo break-all text-text-strong"
          {...testIdProp(`${testIdPrefix}-revoke-dialog-subject`)}
        >
          {email.length > 0 ? email : 'An address we can no longer read'}
        </p>
        <p className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-secondary">
          Their invite link will stop working. You can invite them again at any time — that makes a
          new link, and the old one stays dead.
        </p>

        {error !== null && error.length > 0 && (
          <ErrorMessage testId={`${testIdPrefix}-revoke-dialog-error`}>{error}</ErrorMessage>
        )}

        <div className="mt-8 flex items-center justify-end gap-8">
          <Dialog.Close
            disabled={loading}
            render={<Button appearance="saas" tone="secondary" size="md" />}
            {...testIdProp(`${testIdPrefix}-revoke-dialog-cancel`)}
          >
            Cancel
          </Dialog.Close>
          <Button
            appearance="saas"
            tone="danger"
            size="md"
            loading={loading}
            onClick={onConfirm}
            testId={`${testIdPrefix}-revoke-dialog-confirm`}
          >
            Rescind invite
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
